import React, { useEffect, useState } from 'react';
import {
    Table,
    Button,
    Space,
    Tag,
    Modal,
    Descriptions,
    Select,
    message,
    Input,
    Tooltip,
    Card,
    Row,
    Col,
    Statistic,
} from 'antd';
import {
    SearchOutlined,
    CheckOutlined,
    CloseOutlined,
    EyeOutlined,
    FilePdfFilled,
} from '@ant-design/icons';
import { apiCall } from '../../API/RestApi';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { exportAssetRequest } from './AssetRequestExcel';

const { Option } = Select;

const AssetApprovalRequest = () => {
    const token = useSelector((state) => state.user.token);
    const navigate = useNavigate();

    const [data, setData] = useState([]);
    const [meta, setMeta] = useState({ total: 0, page: 1, limit: 10 });
    const [loading, setLoading] = useState(false);

    const [analytics, setAnalytics] = useState({
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
        assigned: 0,
        returned: 0,
    });

    const [selectedAssetRequest, setSelectedAssetRequest] = useState(null);
    const [detailVisible, setDetailVisible] = useState(false);

    const [statusFilter, setStatusFilter] = useState('');
    const [approvalStatus, setApprovalStatus] = useState('');
    const [reason, setReason] = useState('');
    const [editModalVisible, setEditModalVisible] = useState(false);

    const [selectedAssetId, setSelectedAssetId] = useState(null);
    const [availableAssets, setAvailableAssets] = useState([]);
    const [assetLoading, setAssetLoading] = useState(false);

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [bulkModalVisible, setBulkModalVisible] = useState(false);
    const [bulkStatus, setBulkStatus] = useState('');
    const [bulkReason, setBulkReason] = useState('');
    const [bulkAssetMap, setBulkAssetMap] = useState({});

    const [search, setSearch] = useState('');
    const [sorter, setSorter] = useState({
        field: 'asset_assignment_id',
        order: 'descend',
    });

    const getHeaders = () => ({
        Authorization: 'Bearer ' + token,
    });

    const dateFormat = (value) => {
        if (!value) return '-';
        return dayjs(value).format('YYYY-MM-DD');
    };

    const getApprovalStatus = (record) => {
        return record?.approval_status || record?.approval?.status || '-';
    };

    const getDelegationStatus = (record) => {
        return record?.delegation_status || record?.approval?.delegation_status || '-';
    };

    const getRequesterName = (record) => {
        return record?.user?.name || record?.approval?.user?.name || '-';
    };

    const getRequesterCode = (record) => {
        return record?.user?.users_code || record?.approval?.user?.users_code || '';
    };

    const getRequesterPosition = (record) => {
        return record?.user?.position?.position_name || record?.approval?.user?.position?.position_name || '-';
    };

    const getApprovalUserName = (record) => {
        return record?.approval?.approval_user?.name || '-';
    };

    const getApprovalUserPosition = (record) => {
        return record?.approval?.approval_user?.position?.position_name || '-';
    };

    const getDelegationUserName = (record) => {
        return record?.approval?.delegation_user?.name || '-';
    };

    const getCategoryName = (record) => {
        return (
            record?.category?.name ||
            record?.category?.asset_category_name ||
            record?.asset?.category?.name ||
            record?.asset?.category?.asset_category_name ||
            '-'
        );
    };

    const getCategoryId = (record) => {
        return (
            record?.asset_category_id ||
            record?.category?.asset_category_id ||
            record?.asset?.asset_category_id ||
            record?.asset?.category?.asset_category_id ||
            null
        );
    };

    const renderApprovalTag = (status) => {
        if (status === 'Waiting Authorization') return <Tag color="purple">Waiting Authorization</Tag>;
        if (status === 'Pending') return <Tag color="orange">Pending Final</Tag>;
        if (status === 'Approved') return <Tag color="green">Approved</Tag>;
        if (status === 'Rejected') return <Tag color="red">Rejected</Tag>;
        return <Tag>{status || '-'}</Tag>;
    };

    const renderDelegationTag = (status) => {
        if (status === 'Approved') return <Tag color="green">Approved</Tag>;
        if (status === 'Pending') return <Tag color="orange">Pending</Tag>;
        if (status === 'Rejected') return <Tag color="red">Rejected</Tag>;
        if (status === 'Skipped') return <Tag color="blue">Skipped</Tag>;
        return <Tag>{status || '-'}</Tag>;
    };

    const renderStatusTag = (status) => {
        if (status === 'assigned') return <Tag color="blue">Assigned</Tag>;
        if (status === 'pending') return <Tag color="orange">Pending</Tag>;
        if (status === 'returned') return <Tag color="green">Returned</Tag>;
        if (status === 'rejected') return <Tag color="red">Rejected</Tag>;
        return <Tag>{status || '-'}</Tag>;
    };

    const openReceipt = (record, type = 'ISSUE') => {
        if (!record?.asset_assignment_id) return;

        navigate('/assetapproval/receipt', {
            state: {
                id: record.asset_assignment_id,
                type,
            },
        });
    };

    const fetchAvailableAssets = async (record = null) => {
        setAssetLoading(true);

        try {
            const categoryId = getCategoryId(record);

            const params = new URLSearchParams({
                page: 1,
                limit: 100,
                asset_status: 'Active',
            });

            if (categoryId) {
                params.append('asset_category_id', categoryId);
            }

            const res = await apiCall({
                method: 'GET',
                url: `/assets?${params.toString()}`,
                data: {
                    headers: getHeaders(),
                },
            });

            if (res.status === 'success') {
                const rows = res?.data?.data || res?.data || [];
                setAvailableAssets(Array.isArray(rows) ? rows : []);
            } else {
                setAvailableAssets([]);
                message.error(res.message || 'Gagal mengambil data asset');
            }
        } catch (error) {
            setAvailableAssets([]);
            message.error(error?.response?.data?.message || 'Gagal mengambil data asset');
        } finally {
            setAssetLoading(false);
        }
    };

    const fetchData = async (page = 1, limit = 10, sort = sorter) => {
        setLoading(true);

        try {
            const sortOrder = sort.order === 'ascend' ? 'asc' : 'desc';

            const params = new URLSearchParams({
                page,
                limit,
                search: search || '',
                sortBy: sort.field || 'asset_assignment_id',
                order: sortOrder,
                approval_status: statusFilter || '',
            });

            const res = await apiCall({
                method: 'GET',
                url: `/asset-assignments?${params.toString()}`,
                data: {
                    headers: getHeaders(),
                },
            });

            if (res.status === 'success') {
                setData(res.data.data || []);

                setMeta({
                    page: res.data.page || 1,
                    limit: res.data.limit || 10,
                    total: res.data.total_data || 0,
                });

                setAnalytics({
                    total: res.data.stats?.total || 0,
                    pending: res.data.stats?.pending || 0,
                    approved: res.data.stats?.approved || 0,
                    rejected: res.data.stats?.rejected || 0,
                    assigned: res.data.stats?.assigned || 0,
                    returned: res.data.stats?.returned || 0,
                });
            } else {
                message.error(res.message || 'Failed to fetch data');
            }
        } catch (error) {
            message.error(error?.response?.data?.message || 'Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            fetchData(1, meta.limit);
        }, 500);

        return () => clearTimeout(timeout);
    }, [search, statusFilter]);

    const handleChangeStatus = async () => {
        if (!selectedAssetRequest?.asset_assignment_id) {
            return message.warning('Data tidak valid');
        }

        if (!approvalStatus) {
            return message.warning('Please select a status');
        }

        if (approvalStatus === 'Approved' && !selectedAssetId) {
            return message.warning('Asset wajib dipilih saat approve');
        }

        try {
            const payload = {
                approval_status: approvalStatus,
                notes: reason || '',
            };

            if (approvalStatus === 'Approved') {
                payload.asset_id = Number(selectedAssetId);
            }

            const res = await apiCall({
                method: 'PUT',
                url: `/asset-assignments/${selectedAssetRequest.asset_assignment_id}/approval`,
                data: {
                    data: payload,
                    headers: {
                        ...getHeaders(),
                        'Content-Type': 'application/json',
                    },
                },
            });

            if (res.status === 'success') {
                message.success(res.message || 'Status updated successfully');
                setEditModalVisible(false);
                setDetailVisible(false);
                setReason('');
                setApprovalStatus('');
                setSelectedAssetId(null);
                setSelectedAssetRequest(null);
                setAvailableAssets([]);
                fetchData(meta.page, meta.limit);
            } else {
                message.error(res.message || 'Failed to update status');
            }
        } catch (error) {
            message.error(error?.response?.data?.message || 'Failed to change status');
        }
    };

    const openActionModal = async (record, status) => {
        setSelectedAssetRequest(record);
        setApprovalStatus(status);
        setReason('');
        setSelectedAssetId(record?.asset_id || null);
        setEditModalVisible(true);

        if (status === 'Approved') {
            await fetchAvailableAssets(record);
        }
    };

    const openBulkModal = async (status) => {
        setBulkStatus(status);
        setBulkReason('');
        setBulkAssetMap({});
        setBulkModalVisible(true);

        if (status === 'Approved') {
            await fetchAvailableAssets(null);
        }
    };

    const handleBulkApproval = async () => {
        if (selectedRowKeys.length === 0) {
            return message.warning('Please select at least one record');
        }

        if (!bulkStatus) {
            return message.warning('Please select a status');
        }

        if (bulkStatus === 'Approved') {
            const missingAsset = selectedRows.some((row) => !bulkAssetMap[row.asset_assignment_id]);

            if (missingAsset) {
                return message.warning('Semua item wajib pilih asset saat bulk approve');
            }
        }

        Modal.confirm({
            title: `${bulkStatus === 'Approved' ? 'Approve' : 'Reject'} ${selectedRowKeys.length} asset assignment?`,
            content: 'Data yang diproses hanya asset assignment dengan approval status Pending.',
            okText: bulkStatus === 'Approved' ? 'Approve' : 'Reject',
            okType: bulkStatus === 'Rejected' ? 'danger' : 'primary',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    setLoading(true);

                    let payload = {
                        approval_status: bulkStatus,
                        notes: bulkReason || '',
                    };

                    if (bulkStatus === 'Approved') {
                        payload.items = selectedRows.map((row) => ({
                            asset_assignment_id: Number(row.asset_assignment_id),
                            asset_id: Number(bulkAssetMap[row.asset_assignment_id]),
                        }));
                    } else {
                        payload.ids = selectedRowKeys.map(Number);
                    }

                    const res = await apiCall({
                        method: 'PUT',
                        url: `/asset-assignments/bulk-approval`,
                        data: {
                            data: payload,
                            headers: {
                                ...getHeaders(),
                                'Content-Type': 'application/json',
                            },
                        },
                    });

                    if (res.status === 'success') {
                        message.success(res.message || 'Bulk approval berhasil diproses');
                        setBulkModalVisible(false);
                        setSelectedRowKeys([]);
                        setSelectedRows([]);
                        setBulkStatus('');
                        setBulkReason('');
                        setBulkAssetMap({});
                        fetchData(meta.page, meta.limit);
                    } else {
                        message.error(res.message || 'Failed to update bulk approval');
                    }
                } catch (error) {
                    message.error(error?.response?.data?.message || 'Failed to update bulk approval');
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    const renderAssetLabel = (asset) => {
        return `${asset.brand || ''} ${asset.model || ''} ${asset.serial_number || ''}`.trim();
    };

    const columns = [
        {
            title: 'No',
            width: 70,
            render: (_, __, index) => (meta.page - 1) * meta.limit + index + 1,
        },
        {
            title: 'Requester',
            width: 180,
            render: (_, record) => getRequesterName(record),
        },
        {
            title: 'Asset',
            width: 240,
            render: (_, record) =>
                record.asset
                    ? `${record.asset.asset_name || '-'}${
                          record.asset.asset_code ? ` - ${record.asset.asset_code}` : ''
                      }`
                    : '-',
        },
        {
            title: 'Category',
            width: 150,
            render: (_, record) => getCategoryName(record),
        },
        {
            title: 'Request Date',
            dataIndex: 'request_date',
            width: 130,
            sorter: true,
            render: (_, record) => dateFormat(record.request_date),
        },
        {
            title: 'Assign Date',
            dataIndex: 'assign_date',
            width: 130,
            render: (_, record) => dateFormat(record.assign_date),
        },
        {
            title: 'Return Date',
            dataIndex: 'return_date',
            width: 130,
            render: (_, record) => dateFormat(record.return_date),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            width: 120,
            render: (status) => renderStatusTag(status),
        },
        {
            title: 'Authorization',
            width: 140,
            render: (_, record) => renderDelegationTag(getDelegationStatus(record)),
        },
        {
            title: 'Approval',
            width: 140,
            sorter: true,
            render: (_, record) => renderApprovalTag(getApprovalStatus(record)),
        },
        {
            title: 'Approved By',
            width: 180,
            render: (_, record) => getApprovalUserName(record),
        },
        {
            title: 'Actions',
            fixed: 'right',
            width: 250,
            render: (_, record) => (
                <Space>
                    <Tooltip title="Detail">
                        <Button
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => {
                                setSelectedAssetRequest(record);
                                setDetailVisible(true);
                            }}
                        />
                    </Tooltip>

                    <Tooltip title="Surat Terima">
                        <Button
                            size="small"
                            type="primary"
                            icon={<FilePdfFilled />}
                            disabled={getApprovalStatus(record) !== 'Approved'}
                            onClick={() => openReceipt(record, 'ISSUE')}
                        />
                    </Tooltip>

                    <Tooltip title="Surat Pengembalian">
                        <Button
                            size="small"
                            icon={<FilePdfFilled />}
                            disabled={record.status !== 'returned'}
                            onClick={() => openReceipt(record, 'RETURN')}
                        />
                    </Tooltip>

                    <Tooltip title="Approve">
                        <Button
                            size="small"
                            type="primary"
                            icon={<CheckOutlined />}
                            disabled={getApprovalStatus(record) !== 'Pending'}
                            onClick={() => openActionModal(record, 'Approved')}
                        />
                    </Tooltip>

                    <Tooltip title="Reject">
                        <Button
                            size="small"
                            danger
                            icon={<CloseOutlined />}
                            disabled={getApprovalStatus(record) !== 'Pending'}
                            onClick={() => openActionModal(record, 'Rejected')}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows);
        },
        getCheckboxProps: (record) => ({
            disabled: getApprovalStatus(record) !== 'Pending',
        }),
    };

    const ApprovalLine = ({ data }) => {
        const approval = data?.approval;

        const steps = [
            {
                title: 'Requester',
                name: data?.user?.name || approval?.user?.name || '-',
                status: 'Submitted',
                date: data?.created_at,
                notes: data?.notes || approval?.notes,
            },
            {
                title: 'Authorization',
                name: approval?.delegation_user?.name || '-',
                status: approval?.delegation_status || '-',
                date: approval?.delegation_date,
                notes: approval?.delegation_notes,
            },
            {
                title: 'Final Approval',
                name: approval?.approval_user?.name || '-',
                status: approval?.status || '-',
                date: approval?.approval_date,
                notes: approval?.approval_notes,
            },
        ];

        const colorByStatus = (status) => {
            if (status === 'Approved' || status === 'Submitted') return '#52c41a';
            if (status === 'Pending' || status === 'Waiting Authorization') return '#faad14';
            if (status === 'Rejected') return '#ff4d4f';
            if (status === 'Skipped') return '#1677ff';
            return '#d9d9d9';
        };

        return (
            <Card title="Approval Line" size="small" style={{ marginTop: 16 }}>
                <Row gutter={16}>
                    {steps.map((item, index) => (
                        <Col span={8} key={item.title}>
                            <div
                                style={{
                                    border: '1px solid #f0f0f0',
                                    borderRadius: 10,
                                    padding: 14,
                                    minHeight: 150,
                                    position: 'relative',
                                    background: '#fff',
                                }}
                            >
                                {index < steps.length - 1 && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: 32,
                                            right: -16,
                                            width: 16,
                                            height: 2,
                                            background: '#d9d9d9',
                                        }}
                                    />
                                )}

                                <div
                                    style={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: '50%',
                                        background: colorByStatus(item.status),
                                        color: '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: 700,
                                        marginBottom: 10,
                                    }}
                                >
                                    {index + 1}
                                </div>

                                <div style={{ fontWeight: 700, marginBottom: 4 }}>{item.title}</div>
                                <div style={{ color: '#555', marginBottom: 8 }}>{item.name}</div>

                                <Tag
                                    color={
                                        item.status === 'Approved' || item.status === 'Submitted'
                                            ? 'green'
                                            : item.status === 'Rejected'
                                                ? 'red'
                                                : item.status === 'Skipped'
                                                    ? 'blue'
                                                    : 'orange'
                                    }
                                >
                                    {item.status}
                                </Tag>

                                <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                                    {item.date ? dayjs(item.date).format('YYYY-MM-DD HH:mm') : '-'}
                                </div>

                                {item.notes && (
                                    <div style={{ marginTop: 8, fontSize: 12, color: '#555' }}>
                                        “{item.notes}”
                                    </div>
                                )}
                            </div>
                        </Col>
                    ))}
                </Row>
            </Card>
        );
    };

    return (
        <div>
            <h2>Asset Approval</h2>

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={24} sm={12} md={8} lg={4}>
                    <Card>
                        <Statistic title="Total Data" value={analytics.total} />
                    </Card>
                </Col>

                <Col xs={24} sm={12} md={8} lg={4}>
                    <Card>
                        <Statistic title="Pending Final" value={analytics.pending} />
                    </Card>
                </Col>

                <Col xs={24} sm={12} md={8} lg={4}>
                    <Card>
                        <Statistic title="Approved" value={analytics.approved} />
                    </Card>
                </Col>

                <Col xs={24} sm={12} md={8} lg={4}>
                    <Card>
                        <Statistic title="Rejected" value={analytics.rejected} />
                    </Card>
                </Col>

                <Col xs={24} sm={12} md={8} lg={4}>
                    <Card>
                        <Statistic title="Assigned" value={analytics.assigned} />
                    </Card>
                </Col>

                <Col xs={24} sm={12} md={8} lg={4}>
                    <Card>
                        <Statistic title="Returned" value={analytics.returned} />
                    </Card>
                </Col>
            </Row>

            <Card style={{ marginBottom: 16 }}>
                <Space wrap align="start" style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Space wrap>
                        <Input
                            placeholder="Search by asset name, code, brand, or model"
                            value={search}
                            prefix={<SearchOutlined />}
                            onChange={(e) => setSearch(e.target.value)}
                            onPressEnter={() => fetchData(1, meta.limit)}
                            allowClear
                            style={{ width: 320 }}
                        />

                        <Select
                            placeholder="Filter by approval"
                            allowClear
                            value={statusFilter || undefined}
                            onChange={(value) => setStatusFilter(value || '')}
                            style={{ width: 200 }}
                        >
                            <Option value="Pending">Pending Final</Option>
                            <Option value="Approved">Approved</Option>
                            <Option value="Rejected">Rejected</Option>
                        </Select>
                    </Space>

                    <Space>
                        <Button onClick={() => exportAssetRequest(data)}>Export Excel</Button>

                        <Button
                            type="primary"
                            icon={<CheckOutlined />}
                            disabled={selectedRowKeys.length === 0}
                            onClick={() => openBulkModal('Approved')}
                        >
                            Approve
                        </Button>

                        <Button
                            danger
                            icon={<CloseOutlined />}
                            disabled={selectedRowKeys.length === 0}
                            onClick={() => openBulkModal('Rejected')}
                        >
                            Reject
                        </Button>
                    </Space>
                </Space>
            </Card>

            <Table
                rowSelection={rowSelection}
                columns={columns}
                dataSource={data}
                rowKey="asset_assignment_id"
                loading={loading}
                scroll={{ x: 1700 }}
                pagination={{
                    current: meta.page,
                    pageSize: meta.limit,
                    total: meta.total,
                    showSizeChanger: true,
                }}
                onChange={(pagination, filters, sorterObj) => {
                    const sort = {
                        field: sorterObj.field || 'asset_assignment_id',
                        order: sorterObj.order || 'descend',
                    };

                    setSorter(sort);
                    fetchData(pagination.current, pagination.pageSize, sort);

                    setMeta((prev) => ({
                        ...prev,
                        page: pagination.current,
                        limit: pagination.pageSize,
                    }));
                }}
            />

            <Modal
                title="Asset Request Detail"
                open={detailVisible}
                onCancel={() => setDetailVisible(false)}
                footer={[
                    <div
                        key="footer"
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                        }}
                    >
                        <Space>
                            <Button
                                type="primary"
                                icon={<FilePdfFilled />}
                                disabled={getApprovalStatus(selectedAssetRequest) !== 'Approved'}
                                onClick={() => openReceipt(selectedAssetRequest, 'ISSUE')}
                            >
                                Surat Terima
                            </Button>

                            <Button
                                icon={<FilePdfFilled />}
                                disabled={selectedAssetRequest?.status !== 'returned'}
                                onClick={() => openReceipt(selectedAssetRequest, 'RETURN')}
                            >
                                Surat Pengembalian
                            </Button>
                        </Space>

                        <Button onClick={() => setDetailVisible(false)}>Close</Button>
                    </div>,
                ]}
                width={950}
            >
                {selectedAssetRequest && (
                    <Descriptions column={2} bordered size="small">
                        <Descriptions.Item label="Approval Status">
                            {renderApprovalTag(getApprovalStatus(selectedAssetRequest))}
                        </Descriptions.Item>

                        <Descriptions.Item label="Authorization Status">
                            {renderDelegationTag(getDelegationStatus(selectedAssetRequest))}
                        </Descriptions.Item>

                        <Descriptions.Item label="Current Status">
                            {renderStatusTag(selectedAssetRequest.status)}
                        </Descriptions.Item>

                        <Descriptions.Item label="Request Date">
                            {dateFormat(selectedAssetRequest.request_date)}
                        </Descriptions.Item>

                        <Descriptions.Item label="Assign Date">
                            {dateFormat(selectedAssetRequest.assign_date)}
                        </Descriptions.Item>

                        <Descriptions.Item label="Return Date">
                            {dateFormat(selectedAssetRequest.return_date)}
                        </Descriptions.Item>

                        <Descriptions.Item label="Requester">
                            {getRequesterName(selectedAssetRequest)}
                            {getRequesterCode(selectedAssetRequest)
                                ? ` (${getRequesterCode(selectedAssetRequest)})`
                                : ''}
                        </Descriptions.Item>

                        <Descriptions.Item label="Requester Position">
                            {getRequesterPosition(selectedAssetRequest)}
                        </Descriptions.Item>

                        <Descriptions.Item label="Authorization By">
                            {getDelegationUserName(selectedAssetRequest)}
                        </Descriptions.Item>

                        <Descriptions.Item label="Approved By">
                            {getApprovalUserName(selectedAssetRequest)}
                        </Descriptions.Item>

                        <Descriptions.Item label="Approver Position">
                            {getApprovalUserPosition(selectedAssetRequest)}
                        </Descriptions.Item>

                        <Descriptions.Item label="Asset Name">
                            {selectedAssetRequest.asset?.asset_name || '-'}
                        </Descriptions.Item>

                        <Descriptions.Item label="Asset Code">
                            {selectedAssetRequest.asset?.asset_code || '-'}
                        </Descriptions.Item>

                        <Descriptions.Item label="Serial Number">
                            {selectedAssetRequest.asset?.serial_number || '-'}
                        </Descriptions.Item>

                        <Descriptions.Item label="Brand">
                            {selectedAssetRequest.asset?.brand || '-'}
                        </Descriptions.Item>

                        <Descriptions.Item label="Model">
                            {selectedAssetRequest.asset?.model || '-'}
                        </Descriptions.Item>

                        <Descriptions.Item label="Category">
                            {getCategoryName(selectedAssetRequest)}
                        </Descriptions.Item>

                        <Descriptions.Item label="Notes" span={2}>
                            {selectedAssetRequest.notes || '-'}
                        </Descriptions.Item>

                        <Descriptions.Item label="Authorization Notes" span={2}>
                            {selectedAssetRequest.approval?.delegation_notes || '-'}
                        </Descriptions.Item>

                        <Descriptions.Item label="Approval Notes" span={2}>
                            {selectedAssetRequest.approval?.approval_notes || '-'}
                        </Descriptions.Item>
                    </Descriptions>
                )}

                <ApprovalLine data={selectedAssetRequest} />
            </Modal>

            <Modal
                title={`${approvalStatus === 'Approved' ? 'Approve' : 'Reject'} Asset Request`}
                open={editModalVisible}
                onCancel={() => {
                    setEditModalVisible(false);
                    setSelectedAssetId(null);
                    setAvailableAssets([]);
                }}
                onOk={handleChangeStatus}
                okText="Submit"
                destroyOnClose
            >
                {approvalStatus === 'Approved' && (
                    <div style={{ marginBottom: 16 }}>
                        <label>
                            Asset <span style={{ color: 'red' }}>*</span>
                        </label>

                        <Select
                            showSearch
                            allowClear
                            loading={assetLoading}
                            placeholder="Pilih asset"
                            value={selectedAssetId || undefined}
                            onChange={(value) => setSelectedAssetId(value || null)}
                            style={{ width: '100%', marginTop: 8 }}
                            optionFilterProp="children"
                        >
                            {availableAssets.map((asset) => (
                                <Option key={asset.asset_id} value={asset.asset_id}>
                                    {renderAssetLabel(asset)}
                                </Option>
                            ))}
                        </Select>
                    </div>
                )}

                <Input.TextArea
                    rows={3}
                    placeholder="Reason / Notes optional"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                />
            </Modal>

            <Modal
                title={`${bulkStatus === 'Approved' ? 'Bulk Approve' : 'Bulk Reject'} Asset Assignment`}
                open={bulkModalVisible}
                onCancel={() => {
                    setBulkModalVisible(false);
                    setBulkAssetMap({});
                }}
                onOk={handleBulkApproval}
                okText="Submit"
                destroyOnClose
                width={bulkStatus === 'Approved' ? 850 : 520}
            >
                <p>
                    <b>{selectedRowKeys.length}</b> item(s) selected
                </p>

                {bulkStatus === 'Approved' && (
                    <div style={{ marginBottom: 16 }}>
                        {selectedRows.map((row) => (
                            <Card
                                key={row.asset_assignment_id}
                                size="small"
                                style={{ marginBottom: 8 }}
                            >
                                <Row gutter={[12, 12]} align="middle">
                                    <Col xs={24} md={8}>
                                        <b>{getRequesterName(row)}</b>
                                        <br />
                                        <small>{getCategoryName(row)}</small>
                                    </Col>

                                    <Col xs={24} md={16}>
                                        <label>
                                            Asset <span style={{ color: 'red' }}>*</span>
                                        </label>

                                        <Select
                                            showSearch
                                            allowClear
                                            loading={assetLoading}
                                            placeholder="Pilih asset"
                                            value={bulkAssetMap[row.asset_assignment_id] || undefined}
                                            onChange={(value) =>
                                                setBulkAssetMap((prev) => ({
                                                    ...prev,
                                                    [row.asset_assignment_id]: value || null,
                                                }))
                                            }
                                            style={{ width: '100%', marginTop: 6 }}
                                            optionFilterProp="children"
                                            status={
                                                bulkStatus === 'Approved' &&
                                                !bulkAssetMap[row.asset_assignment_id]
                                                    ? 'error'
                                                    : ''
                                            }
                                        >
                                            {availableAssets
                                                .filter((asset) => {
                                                    const rowCategoryId = getCategoryId(row);
                                                    const assetCategoryId =
                                                        asset.asset_category_id ||
                                                        asset.category?.asset_category_id;

                                                    if (!rowCategoryId || !assetCategoryId) return true;

                                                    return Number(rowCategoryId) === Number(assetCategoryId);
                                                })
                                                .map((asset) => (
                                                    <Option key={asset.asset_id} value={asset.asset_id}>
                                                        {renderAssetLabel(asset)}
                                                    </Option>
                                                ))}
                                        </Select>
                                    </Col>
                                </Row>
                            </Card>
                        ))}
                    </div>
                )}

                <Input.TextArea
                    rows={3}
                    placeholder="Reason / Notes optional"
                    value={bulkReason}
                    onChange={(e) => setBulkReason(e.target.value)}
                />
            </Modal>
        </div>
    );
};

export default AssetApprovalRequest;